/**
 * Ce que LAB lit à l'écran, en regard de ce que la base stocke.
 *
 * ADR-0024 : aucune information n'est portée par la couleur seule. Un statut se
 * lit donc EN TOUTES LETTRES, et ces tableaux sont le seul endroit où la
 * traduction est écrite. Un code inconnu se rend tel quel plutôt que disparaître :
 * mieux vaut un libellé laid qu'une case vide qui cache un état réel.
 *
 * Le vocabulaire vient de CONTEXT.md, qui fait autorité.
 */

import type { Famille, Nature } from "@/lib/api";

function traduire(table: Record<string, string>, code: string | null | undefined) {
  if (!code) return "—";
  return table[code] ?? code.replaceAll("_", " ").toLowerCase();
}

export const STATUTS_COMMANDE: Record<string, string> = {
  SIGNEE: "Signée",
  EN_COURS: "En cours",
  CLOTUREE: "Clôturée définitivement",
  ANNULEE: "Annulée",
};

/** Les six libellés du 06/09 (ADR-0016), restitués par `lot_statut_affiche()`. */
export const STATUTS_LOT: Record<string, string> = {
  EN_COURS: "En cours",
  EN_COURS_DE_POSE: "En cours de pose",
  RECEPTIONNE_SANS_RESERVES: "Réceptionné sans réserves",
  RECEPTIONNE_AVEC_RESERVES: "Réceptionné avec réserves",
  CLOTURE_OPERATIONNELLE: "Clôturé",
  ANNULE: "Annulé",
};

// La catégorie n'a pas de table ici : c'est un référentiel administrable
// (ADR-0030), et l'API rend `categorie_libelle` à côté du code.

export const STATUTS_REGLEMENT: Record<string, string> = {
  NON_FACTURE: "Non facturé",
  FACTURE: "Facturé",
  PARTIELLEMENT_ENCAISSE: "Partiellement encaissé",
  SOLDE: "Soldé",
};

// ADR-0031 : une nature de la commande, pas un référentiel.
export const MODES_REMISE: Record<string, string> = {
  EMPORT: "Emport",
  LIVRAISON: "Livraison",
};

export const TYPES_CLIENT: Record<string, string> = {
  PARTICULIER: "Particulier",
  PROFESSIONNEL: "Professionnel",
};

// LOT 2 : on ne commande que chez un fournisseur actif.
export const STATUTS_FOURNISSEUR: Record<string, string> = {
  ACTIF: "Actif",
  SUSPENDU: "Suspendu",
  ANCIEN: "Ancien",
};

// ADR-0003 : l'avancement de la commande fournisseur. L'AR est un fait daté, et
// les problèmes des objets à part : ni l'un ni les autres ne sont un statut.
export const STATUTS_COMMANDE_FOURNISSEUR: Record<string, string> = {
  BROUILLON: "En préparation",
  ENVOYEE: "Envoyée",
  CONFIRMEE: "Confirmée",
  RECEPTION_PARTIELLE: "Réception partielle",
  RECEPTIONNEE: "Réceptionnée",
  CLOTUREE_PARTIELLE: "Clôturée — livraison partielle",
  ANNULEE: "Annulée",
};

// ADR-0003, axe 3 : ce qui peut arriver à une commande fournisseur (ticket #16).
export const TYPES_PROBLEME_FOURNISSEUR: Record<string, string> = {
  RETARD: "Retard",
  CASSE: "Casse",
  ERREUR_REFERENCE: "Erreur de référence",
  MANQUANT: "Manquant",
  LITIGE_TRANSPORT: "Litige transport",
  AUTRE: "Autre",
};

// Ticket #40 : les natures de frais fournisseur, liste fixe (ADR-0044 amendée).
export const NATURES_FRAIS: Record<string, string> = {
  TRANSPORT: "Transport",
  FACTURATION: "Facturation",
  EMBALLAGE: "Emballage",
  AUTRE: "Autre",
};

export const natureFrais = (code?: string | null) => traduire(NATURES_FRAIS, code);
// Ticket #39 : les sortes qu'on lit, dont celle que la base ouvre à la contestation d'un
// écart de prix, et qui ne s'offre pas à la main.
export const TYPES_PROBLEME_FOURNISSEUR_LUS: Record<string, string> = {
  ...TYPES_PROBLEME_FOURNISSEUR,
  ECART_PRIX: "Écart de prix contesté",
  // Ticket #22 : ouvert par la base sur un reçu au-delà du commandé.
  SURPLUS: "Surplus",
};

// ADR-0046 (ticket #22) : pourquoi une quantité reçue n'est pas conforme.
export const MOTIFS_NON_CONFORMITE: Record<string, string> = {
  CASSE: "Casse",
  ERREUR_REFERENCE: "Erreur de référence",
  AUTRE: "Autre",
};
export const motifNonConformite = (code?: string | null) =>
  traduire(MOTIFS_NON_CONFORMITE, code);
// Ticket #23 : ce que le chargé d'affaires décide d'une non-conformité.
export const DECISIONS_NON_CONFORMITE: Record<string, string> = {
  ACCEPTEE: "Acceptée en l'état",
  ACCEPTEE_AVEC_REMISE: "Acceptée avec remise",
  MAINTENUE: "Non-conformité maintenue",
};
export const decisionNonConformite = (code?: string | null) =>
  traduire(DECISIONS_NON_CONFORMITE, code);
export const typeProblemeFournisseur = (code?: string | null) =>
  traduire(TYPES_PROBLEME_FOURNISSEUR_LUS, code);
export const statutCommande = (code?: string | null) => traduire(STATUTS_COMMANDE, code);
export const statutCommandeFournisseur = (code?: string | null) =>
  traduire(STATUTS_COMMANDE_FOURNISSEUR, code);
/** La couleur souligne le mot, elle ne le remplace pas (ADR-0024). */
export const tonStatutCommandeFournisseur = (code: string) =>
  code === "ANNULEE" ? "neutre" : code === "RECEPTIONNEE" ? "succes" : "actif";
// Ticket #76 (ADR-0056) : l'état des conditions commerciales se dit en mots (ADR-0024).
export const ETATS_CONDITIONS: Record<string, string> = {
  A_VALIDER: "À valider",
  EN_VIGUEUR: "En vigueur",
  A_VENIR: "À venir",
  PERIMEE: "Périmée",
};
export const etatConditions = (code?: string | null) => traduire(ETATS_CONDITIONS, code);
export const tonEtatConditions = (code: string) =>
  code === "EN_VIGUEUR" ? "succes" : code === "A_VALIDER" ? "alerte" : "neutre";

export const CUMULS_COMMANDES: Record<string, string> = {
  CUMUL_ACCEPTE: "Cumul de commandes indépendantes accepté",
  COMMANDE_UNIQUE_EXIGEE: "Commande unique exigée",
};
export const cumulCommandes = (code?: string | null) => traduire(CUMULS_COMMANDES, code);

export const statutFournisseur = (code?: string | null) => traduire(STATUTS_FOURNISSEUR, code);
/** La couleur souligne le mot, elle ne le remplace pas (ADR-0024). */
export const tonStatutFournisseur = (code: string) => (code === "ACTIF" ? "succes" : "alerte");
export const statutLot = (code?: string | null) => traduire(STATUTS_LOT, code);
export const statutReglement = (code?: string | null) => traduire(STATUTS_REGLEMENT, code);
export const typeClient = (code?: string | null) => traduire(TYPES_CLIENT, code);

/** Un montant en euros, sans centimes quand il n'y en a pas — c'est le cas courant. */
/** Un pourcentage à la française, deux décimales au plus (ticket #39). */
export const pourcentage = (valeur: number) =>
  `${valeur.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;

export function euros(montant: number | null | undefined) {
  if (montant === null || montant === undefined) return "—";
  return montant.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: Number.isInteger(montant) ? 0 : 2,
  });
}

/** Une date ISO rendue comme on la dit : « 12 mars 2026 ». */
export function dateLongue(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Une date de la commande fournisseur : `05/11/2026 – S45` (décision 124 b). LAB raisonne en
 * semaines : la semaine est la semaine ISO 8601 (lundi au dimanche ; la première est celle
 * du premier jeudi), dont l'année peut différer de l'année civile. Les autres écrans gardent
 * `dateCourte`.
 */
export function dateSemaine(iso: string | null | undefined) {
  if (!iso) return "—";
  // Un horodatage (la réception) se lit au jour local, comme `horodatage` ; une date, telle quelle.
  const jour = iso.includes("T") ? jourLocal(new Date(iso)) : iso;
  return `${dateCourte(jour)} – S${String(semaineIso(jour)).padStart(2, "0")}`;
}

function jourLocal(date: Date) {
  const deux = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${deux(date.getMonth() + 1)}-${deux(date.getDate())}`;
}

/** Le numéro de semaine ISO 8601 d'une date `AAAA-MM-JJ`, sans passer par le fuseau local. */
export function semaineIso(iso: string) {
  const [annee, mois, jour] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(annee, mois - 1, jour));
  // Le jeudi de la semaine dit à quelle année ISO elle appartient.
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const debutAnnee = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - debutAnnee) / 86_400_000 + 1) / 7);
}

/** Une quantité à la française, jusqu'au millième : « 2,5 ». */
export function quantiteLisible(valeur: number) {
  return valeur.toLocaleString("fr-FR", { maximumFractionDigits: 3 });
}

export function dateCourte(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR");
}

/**
 * La date du jour, au format `AAAA-MM-JJ`, en heure LOCALE.
 *
 * `new Date().toISOString()` rend l'UTC : à 0 h 30 à Paris, il renvoie la
 * VEILLE. Un bon de commande saisi en fin de soirée porterait alors la date de
 * la veille, et personne ne le remarquerait avant l'audit.
 */
export function aujourdHui() {
  const maintenant = new Date();
  const local = new Date(maintenant.getTime() - maintenant.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/**
 * Les vingt-cinq types de documents d'avant l'ADR-0062, en clair. Les types vivent
 * désormais en base (`types_document`) et l'écran des documents y lit leur libellé ;
 * cette table ne sert plus que de repli, et pour les types lus par l'OCR, qui restent
 * une liste fermée (`profils_types_ocr`).
 *
 * Les trois BL sont désambiguïsés ici, parce que le mot seul ne veut rien dire :
 * `BL_FOURNISSEUR` va du fournisseur vers LAB à la réception en dépôt, `BL` va
 * de LAB vers le client à la livraison, et `BL_SIGNE` est le même, signé.
 * CONTEXT.md déconseille explicitement le mot « BL » employé seul.
 */
export const CATEGORIES_DOCUMENT: Record<string, string> = {
  BDC_SIGNE: "Bon de commande signé",
  DEVIS: "Devis",
  AVENANT: "Avenant",
  PLAN: "Plan",
  PLAN_VALIDE: "Plan validé",
  RENDU_3D: "Rendu 3D",
  AR_FOURNISSEUR: "Accusé de réception fournisseur",
  BL_FOURNISSEUR: "Bon de livraison fournisseur (à réception)",
  PHOTO_RECEPTION_LOGISTIQUE: "Photo de réception en dépôt",
  BL: "Bon de livraison (vers le client)",
  BL_SIGNE: "Bon de livraison signé par le client",
  // ADR-0033 : la preuve de réalisation d'une vente à l'emport sans pose.
  BON_ENLEVEMENT_SIGNE: "Bon d'enlèvement signé par le client",
  PHOTO_LIVRAISON: "Photo de livraison",
  PHOTO_CHANTIER: "Photo de chantier",
  PV_RECEPTION: "PV de réception",
  PHOTO_RESERVE_AVANT: "Photo de réserve — avant",
  PHOTO_RESERVE_APRES: "Photo de réserve — après",
  PV_LEVEE_RESERVES: "PV de levée de réserves",
  FICHE_TVA_10: "Fiche TVA à 10 %",
  FACTURE_FOURNISSEUR: "Facture fournisseur",
  FACTURE_POSEUR: "Facture poseur",
  FACTURE_TRANSPORTEUR: "Facture transporteur",
  FACTURE_SAV: "Facture SAV",
  FACTURE_CLIENT_SAGE: "Facture client (Sage)",
  AUTRE: "Autre",
};

/** Un collègue tel qu'on le nomme à l'écran. */
export const nomPersonne = (personne: { prenom: string; nom: string }) =>
  `${personne.prenom} ${personne.nom}`;

export const categorieDocument = (code?: string | null) => traduire(CATEGORIES_DOCUMENT, code);

/** Une taille de fichier lisible : « 2,4 Mo » plutôt que « 2516582 ». */
export function octets(taille: number) {
  if (taille < 1024) return `${taille} o`;
  if (taille < 1024 * 1024) return `${Math.round(taille / 1024)} Ko`;
  return `${(taille / (1024 * 1024)).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`;
}

/** Un horodatage complet, rendu comme on le dit. */
export function horodatage(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Les civilités proposées, en regard de `CIVILITES` côté serveur.
 *
 * Deux seulement : « Mlle » a disparu des formulaires administratifs français
 * en 2012 et LAB ne la propose pas. Le serveur refuse tout le reste — cette
 * liste guide la saisie, elle ne la garantit pas.
 */
export const OPTIONS_CIVILITE = [
  { valeur: "M.", libelle: "M." },
  { valeur: "Mme", libelle: "Mme" },
];

export const CIVILITE_PAR_DEFAUT = "M.";

// ADR-0032 : le niveau d'une habilitation.
export const NIVEAUX_ACCES: Record<string, string> = {
  CONSULTATION: "Consultation seule",
  INTERVENTION: "Avec intervention",
};
export const niveauAcces = (code?: string | null) => traduire(NIVEAUX_ACCES, code);
export const OPTIONS_NIVEAU_ACCES = Object.entries(NIVEAUX_ACCES).map(([valeur, libelle]) => ({
  valeur,
  libelle,
}));

/**
 * Où en est une habilitation, « droit de niveau 2 » à l'écran, en toutes lettres (ADR-0024).
 * `au` nul : sans fin. Les dates ISO se comparent comme des chaînes.
 */
export function etatAcces(acces: {
  du: string | null;
  au: string | null;
  en_cours: boolean;
  revoquee_le?: string | null;
}): string {
  if (acces.revoquee_le) return "Révoqué";
  if (acces.en_cours) return "En cours";
  if (acces.du && acces.du > aujourdHui()) return "À venir";
  return "Terminé";
}

/** « du 5 octobre 2026 au 9 octobre 2026 », ou « à partir du … » sans fin. */
export function periode(du: string | null, au: string | null): string {
  if (!du) return "—";
  return au ? `du ${dateLongue(du)} au ${dateLongue(au)}` : `à partir du ${dateLongue(du)}`;
}

/**
 * Les trois familles de périmètre (ADR-0035), telles que l'Administrateur les
 * choisit. Elles sont emboîtées, et ne se paramètrent pas : le texte dit ce que
 * chacune ouvre, parce que le mot seul ne suffit pas à le deviner.
 */
export const FAMILLES: Record<Famille, { libelle: string; detail: string }> = {
  AFFECTATIONS: {
    libelle: "Ses affectations",
    detail: "Les lots, livraisons et interventions qu'on lui confie, et rien d'autre.",
  },
  SES_COMMANDES: {
    libelle: "Ses commandes",
    detail:
      "Ses affectations, plus les commandes dont il est propriétaire et celles où un droit de niveau 2 l'autorise. Son porteur peut se voir rattacher des commandes.",
  },
  TOUT: { libelle: "Tout", detail: "Toutes les commandes, sans en posséder aucune." },
};

export const NATURES: Record<Nature, string> = {
  INTERNE: "Compte interne",
  EXTERNE: "Compte externe",
};

/** Ticket #78 : l'état du franco en un mot, jamais par une couleur seule (ADR-0024). */
export const ETATS_FRANCO: Record<string, string> = {
  SOUS_LE_FRANCO: "Sous le franco",
  FRANCO_ATTEINT: "Franco atteint",
  MONTANT_INCOMPLET: "Montant incomplet",
  SANS_FRANCO: "Pas de franco",
  CONDITIONS_NON_RENSEIGNEES: "Conditions non renseignées",
  SANS_OBJET: "Sans objet",
};
export const etatFranco = (code?: string | null) => traduire(ETATS_FRANCO, code);
export const tonEtatFranco = (code: string) =>
  code === "FRANCO_ATTEINT" ? "succes" : code === "SANS_FRANCO" ? "neutre" : "alerte";
